// 10. HOOK POUR LA GESTION DES ANALYSES
// hooks/useAnalysesManager.ts
import { useState, useCallback } from 'react';
import type { Analyse } from '../types/analyse';
import { toast } from 'react-hot-toast';
import { useConfirm } from '../context/ConfirmContext';

export const useAnalysesManager = (
  analyses: Analyse[],
  onAnalysesChange: (analyses: Analyse[]) => void
) => {
  const [editAnalyseIndex, setEditAnalyseIndex] = useState<number | null>(null);
  const [analyseForm, setAnalyseForm] = useState<Analyse>({
    id: 0,
    label: "",
    reason: "",
    diagnostics: "",
    conseils: "",
    nom: "",
  });

  const { confirm } = useConfirm();

  const resetAnalyseForm = useCallback(() => {
    setAnalyseForm({
      id: 0,
      label: "",
      reason: "",
      diagnostics: "",
      conseils: "",
      nom: "",
    });
  }, []);

  const handleAddAnalyse = useCallback(() => {
    resetAnalyseForm();
    setEditAnalyseIndex(null);
    return true; // Indique qu'on doit ouvrir la modal
  }, [resetAnalyseForm]);

  const handleEditAnalyse = useCallback((index: number) => {
    const analyse = analyses[index];
    setAnalyseForm(analyse);
    setEditAnalyseIndex(index);
    return true; // Indique qu'on doit ouvrir la modal
  }, [analyses]);

  const handleAnalyseSubmit = useCallback(() => {
    if (analyseForm.label) { // ✅ seul le nom est obligatoire
      const newAnalyse: Analyse = {
        ...analyseForm,
        id: editAnalyseIndex !== null ? analyseForm.id : Date.now(),
        nom: typeof analyseForm.label === "string" ? analyseForm.label : (analyseForm.nom || ""),
      };

      if (editAnalyseIndex !== null) {
        const updatedAnalyses = [...analyses];
        updatedAnalyses[editAnalyseIndex] = newAnalyse;
        onAnalysesChange(updatedAnalyses);
        toast.success("Analyse modifiée avec succès");
      } else {
        onAnalysesChange([...analyses, newAnalyse]);
        toast.success("Analyse ajoutée avec succès");
      }

      resetAnalyseForm();
      setEditAnalyseIndex(null);
      return true; // Indique qu'on peut fermer la modal
    } else {
      toast.error("Veuillez renseigner au minimum le nom de l'examen.");
      return false;
    }
  }, [analyseForm, editAnalyseIndex, analyses, onAnalysesChange, resetAnalyseForm]);

  // Nouvelle fonction pour ajouter plusieurs analyses à la fois
  const handleAnalysesSubmitMultiple = useCallback((newAnalyses: Analyse[]) => {
    if (newAnalyses.length > 0) {
      // Ajouter un ID unique à chaque analyse
      const analysesWithIds = newAnalyses.map((analyse, index) => ({
        ...analyse,
        id: Date.now() + index,
      }));

      onAnalysesChange([...analyses, ...analysesWithIds]);
      toast.success(`${newAnalyses.length} analyse${newAnalyses.length > 1 ? 's' : ''} ajoutée${newAnalyses.length > 1 ? 's' : ''} avec succès`);

      resetAnalyseForm();
      setEditAnalyseIndex(null);
      return true;
    }
    return false;
  }, [analyses, onAnalysesChange, resetAnalyseForm]);

  const handleDeleteAnalyse = useCallback(async (index: number) => {
    const isConfirmed = await confirm("Êtes-vous sûr de vouloir supprimer cette analyse ?", {
      title: "Supprimer l'analyse",
      confirmText: "Supprimer",
      cancelText: "Annuler",
      isDangerous: true
    });

    if (isConfirmed) {
      const updatedAnalyses = analyses.filter((_, i) => i !== index);
      onAnalysesChange(updatedAnalyses);
      toast.success("Analyse supprimée");
    }
  }, [analyses, onAnalysesChange, confirm]);

  const handleCloseAnalyseModal = useCallback(() => {
    resetAnalyseForm();
    setEditAnalyseIndex(null);
  }, [resetAnalyseForm]);

  return {
    editAnalyseIndex,
    analyseForm,
    setAnalyseForm,
    handleAddAnalyse,
    handleEditAnalyse,
    handleAnalyseSubmit,
    handleAnalysesSubmitMultiple,
    handleDeleteAnalyse,
    handleCloseAnalyseModal,
  };
};
